export const DISPLAY_HOLIDAY_WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const SITE_DISPLAY_HOLIDAY_CLOSED_MESSAGE = "해당일은 지점 휴무일 입니다";

export const DISPLAY_HOLIDAY_WEEKDAY_LABELS = {
  sun: "일",
  mon: "월",
  tue: "화",
  wed: "수",
  thu: "목",
  fri: "금",
  sat: "토",
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const OFFDAY_MEMOS = {
  all: "지점 전체 휴일",
  reservation: "예약상품 전체 휴무",
  lodging: "숙박상품 전체 휴무",
};

const normalizeText = (value, fallback = "") => {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized || fallback;
};

export function formatLocalDateValue(value) {
  if (!value) return "";

  if (typeof value === "string") {
    const matched = value.match(DATE_ONLY_PATTERN);
    if (matched) {
      const year = Number(matched[1]);
      const month = Number(matched[2]);
      const day = Number(matched[3]);
      const parsed = new Date(year, month - 1, day);
      if (
        parsed.getFullYear() === year &&
        parsed.getMonth() === month - 1 &&
        parsed.getDate() === day
      ) {
        return value;
      }
      return "";
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getDisplayHolidayMonthParams(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1 };
  }
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

const getWeekdayKey = (dateValue) => {
  const normalized = formatLocalDateValue(dateValue);
  if (!normalized) return "";
  const [year, month, day] = normalized.split("-").map(Number);
  return DISPLAY_HOLIDAY_WEEKDAYS[new Date(year, month - 1, day).getDay()] || "";
};

const normalizeWeekly = (weekly) => {
  const seen = new Set();
  if (!Array.isArray(weekly)) return [];

  return weekly.filter((weekday) => {
    if (!DISPLAY_HOLIDAY_WEEKDAYS.includes(weekday) || seen.has(weekday)) {
      return false;
    }
    seen.add(weekday);
    return true;
  });
};

const normalizeRows = (rows, { source, memo, publicHoliday = false }) => {
  const seen = new Set();
  if (!Array.isArray(rows)) return [];

  return rows.reduce((acc, row) => {
    const date = formatLocalDateValue(row?.date);
    if (!date || seen.has(date)) return acc;

    seen.add(date);
    acc.push({
      date,
      memo: normalizeText(row?.memo, memo),
      source,
      ...(publicHoliday ? { blocking: false } : {}),
    });
    return acc;
  }, []);
};

export function normalizeDisplayHolidayResponse(response = {}) {
  return {
    site: {
      weekly: normalizeWeekly(response?.site?.weekly),
      dates: normalizeRows(response?.site?.dates, { source: "site", memo: "지점 휴일" }),
    },
    offdays: {
      all: normalizeRows(response?.offdays?.all, { source: "offdays", memo: OFFDAY_MEMOS.all }),
      reservation: normalizeRows(response?.offdays?.reservation, {
        source: "offdays",
        memo: OFFDAY_MEMOS.reservation,
      }),
      lodging: normalizeRows(response?.offdays?.lodging, {
        source: "offdays",
        memo: OFFDAY_MEMOS.lodging,
      }),
    },
    public_holidays: normalizeRows(response?.public_holidays ?? response?.publicHolidays, {
      source: "public_holiday",
      memo: "공휴일",
      publicHoliday: true,
    }),
  };
}

export function getOffdayBucket(data, scope = "all") {
  const normalized = normalizeDisplayHolidayResponse(data);
  if (scope === "reservation" || scope === "lodging" || scope === "all") {
    return normalized.offdays[scope];
  }
  return normalized.offdays.all;
}

export function getDisplayHolidayForDate(dateValue, data, scope = "all") {
  const normalizedDate = formatLocalDateValue(dateValue);
  if (!normalizedDate) return null;

  const normalized = normalizeDisplayHolidayResponse(data);
  const siteDate = normalized.site.dates.find((row) => row.date === normalizedDate);
  if (siteDate) return siteDate;

  const weekday = getWeekdayKey(normalizedDate);
  if (normalized.site.weekly.includes(weekday)) {
    return {
      date: normalizedDate,
      memo: `매주 ${DISPLAY_HOLIDAY_WEEKDAY_LABELS[weekday]}요일`,
      source: "site",
      weekday,
      recurring: true,
    };
  }

  const offday = getOffdayBucket(normalized, scope).find((row) => row.date === normalizedDate);
  if (offday) return offday;

  const publicHoliday = normalized.public_holidays.find((row) => row.date === normalizedDate);
  return publicHoliday || null;
}

export function isSiteDisplayHolidayDate(dateValue, data, scope = "all") {
  return getDisplayHolidayForDate(dateValue, data, scope)?.source === "site";
}

export function getDisplayHolidaySourceLabel(holiday) {
  if (!holiday) return "";
  if (holiday.source === "site") return "지점";
  if (holiday.source === "offdays") return "휴무";
  if (holiday.source === "public_holiday") return "공휴일";
  return "안내";
}

export function formatDisplayHolidayRowText(row) {
  if (!row) return "";
  if (!row.date) return row.memo || "";

  const dateText = row.date.slice(5).replace("-", ".");
  if (row.source === "public_holiday") {
    return dateText;
  }
  return `${dateText} ${row.memo || ""}`.trim();
}

export function getDisplayHolidayRows(
  data,
  scope = "all",
  { includeWeekly = true, includePublicHolidays = true } = {},
) {
  const normalized = normalizeDisplayHolidayResponse(data);
  const rows = [];

  if (includeWeekly) {
    normalized.site.weekly.forEach((weekday) => {
      rows.push({
        key: `site-weekly-${weekday}`,
        date: "",
        memo: `매주 ${DISPLAY_HOLIDAY_WEEKDAY_LABELS[weekday]}요일`,
        source: "site",
        weekday,
        recurring: true,
      });
    });
  }

  const byDate = new Map();
  const pushDateRow = (row) => {
    if (!row?.date || byDate.has(row.date)) return;
    byDate.set(row.date, {
      key: row.date,
      ...row,
    });
  };

  normalized.site.dates.forEach(pushDateRow);
  getOffdayBucket(normalized, scope).forEach(pushDateRow);
  if (includePublicHolidays) {
    normalized.public_holidays.forEach(pushDateRow);
  }

  return [
    ...rows,
    ...Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date)),
  ];
}

export function decorateFlatpickrDay(dayElem, dateValue, data, scope = "all") {
  if (!dayElem) return null;

  dayElem.classList.remove(
    "site-display-holiday-day",
    "site-display-holiday-site",
    "site-display-holiday-recurring",
    "site-display-holiday-offdays",
    "site-display-holiday-public-holiday",
  );
  delete dayElem.dataset.holidayMemo;
  delete dayElem.dataset.holidaySource;
  dayElem.removeAttribute("title");

  const holiday = getDisplayHolidayForDate(dateValue, data, scope);
  if (!holiday) return null;

  // 공휴일은 정상 영업일이므로 캘린더에 별도 표시(dot)하지 않는다
  if (holiday.source === "public_holiday") return null;

  // 종류별 표시 점(dot) 색: 정기휴무=회색, 임시휴무=주황
  const variant = holiday.recurring ? "recurring" : "site";
  dayElem.classList.add("site-display-holiday-day", `site-display-holiday-${variant}`);
  dayElem.dataset.holidayMemo = holiday.memo;
  dayElem.dataset.holidaySource = holiday.source;
  dayElem.setAttribute("title", `${getDisplayHolidaySourceLabel(holiday)}: ${holiday.memo}`);
  return holiday;
}
